/* eslint-disable max-lines-per-function */
import React from "react";
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { Button } from "components/haven-design/buttons";
import {
  QUESTION_TYPES_BUILDER_TAB,
  QUESTIONS_BUILDER_TAB,
  SECTIONS_BUILDER_TAB,
  TREE_BUILDER_TAB,
} from "./form-builder/form-builder-shared";
import FormBuilderQuestionTypesPanel from "./form-builder/form-builder-question-types-panel";
import FormBuilderQuestionsPanel from "./form-builder/form-builder-questions-panel";
import FormBuilderSectionsPanel from "./form-builder/form-builder-sections-panel";
import FormBuilderTreePanel from "./form-builder/form-builder-tree-panel";
import { useFormBuilderStateContext } from "./form-builder/form-builder-state-context";

const FormBuilder: React.FC = () => {
  const {
    draft,
    setDraft,
    parseErrors,
    buildErrors,
    buildWarnings,
    selectedBuilderTab,
    setSelectedBuilderTab,
    selectedQuestionTab,
    setSelectedQuestionTab,
    questionGroupsBySection,
    questionsInActiveTab,
    expandedQuestionIds,
    questionIdentityOptions,
    contextSuggestions,
    questionIdentityLabelMap,
    questionIdentityTypeMap,
    variants,
    reloadFromToml,
    applyBuilderToContent,
    expandVisibleQuestions,
    collapseVisibleQuestions,
    toggleQuestionExpanded,
    deleteQuestion,
    addQuestion,
    updateQuestion,
    applyVariant,
    existingQuestions,
  } = useFormBuilderStateContext();

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title="Visual Form Builder"
        subheader="Define sections, questions, conditionals, and child question links. Use Apply to sync generated TOML."
      />
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            size="L"
            type="button"
            color="secondary"
            onClick={reloadFromToml}
          >
            <Refresh fontSize="small" style={{ marginRight: "0.3rem" }} />
            Reload from TOML
          </Button>
          <Button
            size="L"
            type="button"
            color="primary"
            onClick={async () => {
              await applyBuilderToContent();
            }}
          >
            Apply Builder to TOML
          </Button>
        </Stack>

        {parseErrors.map((error) => (
          <Alert key={error} severity="warning" sx={{ mb: 1 }}>{error}</Alert>
        ))}
        {buildErrors.map((error) => (
          <Alert key={error} severity="error" sx={{ mb: 1 }}>{error}</Alert>
        ))}
        {buildWarnings.map((warning) => (
          <Alert key={warning} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>
        ))}

        <Tabs
          value={selectedBuilderTab}
          onChange={(_, value) => setSelectedBuilderTab(String(value))}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mt: 2, mb: 1 }}
        >
          <Tab value={QUESTION_TYPES_BUILDER_TAB} label="Question Types" />
          <Tab value={QUESTIONS_BUILDER_TAB} label={`Questions (${draft.questions.length})`} />
          <Tab value={SECTIONS_BUILDER_TAB} label={`Sections (${draft.sections.length})`} />
          <Tab value={TREE_BUILDER_TAB} label="Tree View" />
        </Tabs>

        {selectedBuilderTab === QUESTION_TYPES_BUILDER_TAB && (
          <FormBuilderQuestionTypesPanel />
        )}

        {selectedBuilderTab === QUESTIONS_BUILDER_TAB && (
          <FormBuilderQuestionsPanel
            selectedQuestionTab={selectedQuestionTab}
            onSelectQuestionTab={setSelectedQuestionTab}
            questionGroupsBySection={questionGroupsBySection}
            questionsInActiveTab={questionsInActiveTab}
            expandedQuestionIds={expandedQuestionIds}
            onExpandVisibleQuestions={expandVisibleQuestions}
            onCollapseVisibleQuestions={collapseVisibleQuestions}
            onToggleQuestionExpanded={toggleQuestionExpanded}
            onDeleteQuestion={deleteQuestion}
            onAddQuestion={addQuestion}
            onUpdateQuestion={updateQuestion}
            onApplyVariant={applyVariant}
            variants={variants}
            existingQuestions={existingQuestions}
            questionIdentityOptions={questionIdentityOptions}
            contextSuggestions={contextSuggestions}
            questionIdentityLabelMap={questionIdentityLabelMap}
            questionIdentityTypeMap={questionIdentityTypeMap}
          />
        )}

        {selectedBuilderTab === SECTIONS_BUILDER_TAB && (
          <FormBuilderSectionsPanel
            sections={draft.sections}
            questionIdentityLabelMap={questionIdentityLabelMap}
            questionIdentityTypeMap={questionIdentityTypeMap}
            questionGroupsBySection={questionGroupsBySection}
            setDraft={setDraft}
          />
        )}

        {selectedBuilderTab === TREE_BUILDER_TAB && (
          <FormBuilderTreePanel
            questions={draft.questions}
            questionGroupsBySection={questionGroupsBySection}
            existingQuestions={existingQuestions}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default FormBuilder;
